import {
  CheckCircle,
  Info,
  Warning,
  WarningCircle,
  X,
} from '@phosphor-icons/react';
import { useIntl } from 'react-intl';
import { cn } from '../../utils/cn';

type AlertVariant = 'success' | 'info' | 'warning' | 'error';

interface AlertProps {
  /** Semantic variant controlling color, icon, and left-border */
  variant?: AlertVariant;
  /** Optional bold title above the body text */
  title?: string;
  /** Alert body content */
  children: React.ReactNode;
  /** When provided, renders a dismiss (X) button */
  onDismiss?: () => void;
  /** Additional CSS classes */
  className?: string;
}

const variantConfig: Record<
  AlertVariant,
  { bg: string; border: string; text: string; icon: typeof Info }
> = {
  success: {
    bg: 'bg-success-bg',
    border: 'border-l-[3px] border-success',
    text: 'text-success-text',
    icon: CheckCircle,
  },
  info: {
    bg: 'bg-info-bg',
    border: 'border-l-[3px] border-info',
    text: 'text-info-text',
    icon: Info,
  },
  warning: {
    bg: 'bg-warning-bg',
    border: 'border-l-[3px] border-warning',
    text: 'text-warning-text',
    icon: Warning,
  },
  error: {
    bg: 'bg-error-bg',
    border: 'border-l-[3px] border-error',
    text: 'text-error-text',
    icon: WarningCircle,
  },
};

/**
 * Inline notification banner with semantic variants.
 *
 * Each variant renders a matching icon, background color, and colored
 * left border. Optionally dismissible via `onDismiss`.
 *
 * Accessible: uses `role="alert"` so screen readers announce content.
 */
export function Alert({
  variant = 'info',
  title,
  children,
  onDismiss,
  className,
}: AlertProps) {
  const intl = useIntl();
  const config = variantConfig[variant];
  const Icon = config.icon;

  return (
    <div
      role="alert"
      className={cn(
        'flex items-start gap-3 rounded-lg p-4',
        config.bg,
        config.border,
        className,
      )}
    >
      <Icon size={20} weight="fill" className={cn('shrink-0 mt-0.5', config.text)} />
      <div className="flex-1 min-w-0">
        {title && (
          <p className={cn('text-sm font-semibold mb-0.5', config.text)}>
            {title}
          </p>
        )}
        <div className="text-sm text-text-primary">{children}</div>
      </div>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          className="shrink-0 text-text-tertiary hover:text-text-primary transition-colors cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-focus rounded min-h-[var(--touch-target-min)] min-w-[var(--touch-target-min)] flex items-center justify-center lg:min-h-0 lg:min-w-0"
          aria-label={intl.formatMessage({ id: 'a11y.dismissAlert' })}
        >
          <X size={16} />
        </button>
      )}
    </div>
  );
}

export default Alert;
